/* eslint-disable no-underscore-dangle */
import { useMemo, useCallback, useEffect } from 'react';
import { Col, Row, Card, Button, ButtonGroup, ButtonToolbar } from 'react-bootstrap';
import { useQuery, NetworkStatus } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import {
  MapContainer,
  LayersControl,
  ScaleControl,
  // TileLayer,
  // Marker,
  // Popup,
  // Circle,
  FeatureGroup,
  useMapEvents,
  // useMap,
  GeoJSON,
} from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import { booleanContains } from '@turf/boolean-contains';

import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import objectHash from 'object-hash';
import { RefreshCw as RefreshCwIcon } from 'react-feather';

import compact from 'lodash.compact';

import { settingsSet } from '../../store/settings_slice';
import {
  renderOverlay,
  renderOffline,
  renderError,
  renderDateString,
} from '../../components/render_helpers';
import { noiseMapPageQuery } from '../../graphql/map_queries';

import {
  boundary,
  noiseBoundary,
  dualMeasurementZoneOne,
  dualMeasurementZoneTwo,
  dualMeasurementZoneThree,
  dualMeasurementZoneFour,
} from './noise_management_features';

const siteMapTableLabelZoomBreakpoint = 20;

const addTableLabel = (map, layer, name) => {
  const label = window.L.marker(layer.getBounds().getCenter(), {
    icon: window.L.divIcon({
      className: 'label.table-label',
      html: name,
      iconSize: [65, 20],
    }),
  });
  label.addTo(map);
};

const SiteMapEvents = ({
  noiseMapCenterLat,
  noiseMapCenterLng,
  noiseMapZoom,
  onZoomend,
  onMoveend,
}) => {
  // const map = useMap();
  const map = useMapEvents({
    moveend() {
      const currentCenter = map.getCenter();
      onMoveend(currentCenter.lat, currentCenter.lng);
    },
    zoomend() {
      const currentZoom = map.getZoom();
      if (currentZoom < siteMapTableLabelZoomBreakpoint) {
        map.eachLayer((l) => {
          if (!l.feature && l._icon && l._icon.classList.contains('label.table-label')) {
            map.removeLayer(l);
          }
        });
      }
      if (currentZoom === siteMapTableLabelZoomBreakpoint) {
        map.eachLayer((layer) => {
          const { feature } = layer;
          if (feature) {
            const { properties } = feature || {};
            const { name, tableId, pileId } = properties || {};
            if (tableId && !pileId) {
              addTableLabel(map, layer, name);
            }
          }
        });
      }
      onZoomend(currentZoom);
    },
  });
  useEffect(() => {
    map.setView(new window.L.LatLng(noiseMapCenterLat, noiseMapCenterLng), noiseMapZoom);
  }, [map, noiseMapCenterLat, noiseMapCenterLng, noiseMapZoom]);
  return null;
};

const NoiseManagementMap = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const settingsMutating = useSelector((state) => state.settings.mutating);
  const settingsOnline = useSelector((state) => state.settings.online);
  const noiseMapCenterLat = useSelector((state) => state.settings.noiseMapCenterLat);
  const noiseMapCenterLng = useSelector((state) => state.settings.noiseMapCenterLng);
  const noiseMapZoom = useSelector((state) => state.settings.noiseMapZoom);

  const {
    data: pageData,
    loading: pageLoading,
    error: pageError,
    refetch: pageRefetch,
    networkStatus: pageNetworkStatus,
  } = useQuery(noiseMapPageQuery, {
    notifyOnNetworkStatusChange: true,
  });

  const pageLoadedOrRefetching = useMemo(
    () => !pageLoading || (pageLoading && pageNetworkStatus === NetworkStatus.refetch),
    [pageLoading, pageNetworkStatus]
  );

  const tablesFeatureCollection = useMemo(() => {
    if (pageData?.tableGeoJsonList) {
      const features = pageData.tableGeoJsonList.geojson.features.map((t) => {
        const { type, properties, geometry } = t;
        const { type: geometryType, coordinates } = geometry;
        const newCoordinates = coordinates[0].map(([lat, lng]) => [lng, lat]);
        // postgis created geojson comes through in lat/lng
        const newGeometry = {
          type: geometryType,
          coordinates: [newCoordinates],
        };
        const noiseControlled =
          booleanContains(dualMeasurementZoneOne, newGeometry) ||
          booleanContains(dualMeasurementZoneTwo, newGeometry) ||
          booleanContains(dualMeasurementZoneThree, newGeometry) ||
          booleanContains(dualMeasurementZoneFour, newGeometry);
        return {
          type,
          geometry,
          properties: {
            ...properties,
            noiseControlled,
          },
        };
      });
      return {
        type: 'FeatureCollection',
        features,
      };
    }
    return null;
  }, [pageData]);

  const tablesFeatureCollectionObjectHash = useMemo(() => {
    if (tablesFeatureCollection) {
      return objectHash(tablesFeatureCollection);
    }
    return null;
  }, [tablesFeatureCollection]);

  const pilingMachinesFeatureCollection = useMemo(() => {
    if (pageData?.pilingMachineList) {
      const features = pageData?.pilingMachineList.map((pm) => {
        const { geom, name, naskuLastOnlineAt, naskuMachineStatus } = pm;
        return {
          type: 'Feature',
          properties: {
            name,
            naskuLastOnlineAt,
            naskuMachineStatus,
          },
          geometry: geom,
        };
      });
      return {
        type: 'FeatureCollection',
        features,
      };
    }
    return null;
  }, [pageData]);

  const pilingMachinesFeatureCollectionObjectHash = useMemo(() => {
    if (pilingMachinesFeatureCollection) {
      return objectHash(pilingMachinesFeatureCollection);
    }
    return null;
  }, [pilingMachinesFeatureCollection]);

  const tableMarkerOptions = useMemo(
    () => ({
      weight: 1,
      opacity: 1,
      fillOpacity: 0.5,
      color: 'white',
      fillColor: 'white',
    }),
    []
  );

  const getTableMarkerOptions = useCallback(
    (feature) => {
      const { properties } = feature;
      const { noiseControlled } = properties || {};
      let color;
      let fillOpacity;
      if (noiseControlled) {
        color = 'green';
        fillOpacity = 0.5;
      }
      return {
        ...tableMarkerOptions,
        ...(color && { color }),
        ...(color && { fillColor: color }),
        ...(fillOpacity && { fillOpacity }),
      };
    },
    [tableMarkerOptions]
  );

  const styleTable = (feature) => getTableMarkerOptions(feature);

  const coordsToLatLng = (coords) => new window.L.LatLng(coords[0], coords[1]);

  const onEachFeaturePilingMachine = (feature, layer) => {
    const { properties } = feature;
    const { name, naskuMachineStatus, naskuLastOnlineAt } = properties || {};
    const tooltip = compact([
      name,
      naskuMachineStatus,
      renderDateString(naskuLastOnlineAt),
    ]).join(' - ');
    layer.on('add', (e) => {
      const addLayer = e.target;
      addLayer.bindTooltip(tooltip, { direction: 'top' });
    });
  };

  // const onDrawCreatePath = (feature) => {
  //   console.log(feature.layer.toGeoJSON());
  // };

  const onDrawEditPath = (features) => {
    console.log(features.layers.toGeoJSON());
  };

  const onZoomend = (newSiteMapZoom) => {
    dispatch(
      settingsSet({
        noiseMapZoom: newSiteMapZoom,
      })
    );
  };

  const onMoveend = (newSiteMapCenterLat, newSiteMapCenterLng) => {
    dispatch(
      settingsSet({
        noiseMapCenterLat: newSiteMapCenterLat,
        noiseMapCenterLng: newSiteMapCenterLng,
      })
    );
  };

  const renderContent = () => (
    <>
      <Row className="mt-4 mb-3">
        <Col>
          <h1 className="h3 mb-3">Noise Management Map</h1>
        </Col>
        <Col>
          <ButtonToolbar className="justify-content-end">
            <ButtonGroup>
              <Button
                variant="primary"
                onClick={() => pageRefetch()}
                disabled={!settingsOnline}
              >
                <RefreshCwIcon />
              </Button>
            </ButtonGroup>
          </ButtonToolbar>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <Card.Body>
              <MapContainer
                style={{ height: 'calc(100vh - 215px)' }}
                center={{ lat: noiseMapCenterLat, lng: noiseMapCenterLng }}
                zoom={noiseMapZoom}
                maxZoom={23}
                scrollWheelZoom={false}
              >
                <SiteMapEvents
                  onZoomend={onZoomend}
                  onMoveend={onMoveend}
                  noiseMapCenterLat={noiseMapCenterLat}
                  noiseMapCenterLng={noiseMapCenterLng}
                  noiseMapZoom={noiseMapZoom}
                />
                <ScaleControl position="topleft" imperial={false} />
                <LayersControl>
                  <LayersControl.BaseLayer name="Hybrid Satellite">
                    <ReactLeafletGoogleLayer
                      apiKey="AIzaSyCzoDa5VVrDL4XQPJ1QP35HAdeVZ0OGye8"
                      type="hybrid"
                      maxZoom={23}
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer checked name="Street">
                    <ReactLeafletGoogleLayer
                      apiKey="AIzaSyCzoDa5VVrDL4XQPJ1QP35HAdeVZ0OGye8"
                      type="roadmap"
                      maxZoom={23}
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.Overlay checked name="tables">
                    <GeoJSON
                      key={tablesFeatureCollectionObjectHash}
                      data={tablesFeatureCollection}
                      coordsToLatLng={coordsToLatLng}
                      style={styleTable}
                    />
                  </LayersControl.Overlay>
                  <LayersControl.Overlay checked name="pilingMachines">
                    <GeoJSON
                      key={pilingMachinesFeatureCollectionObjectHash}
                      data={pilingMachinesFeatureCollection}
                      coordsToLatLng={coordsToLatLng}
                      // style={styleTable}
                      onEachFeature={onEachFeaturePilingMachine}
                    />
                  </LayersControl.Overlay>
                  <LayersControl.Overlay checked name="boundary">
                    <GeoJSON
                      key={objectHash(boundary)}
                      data={boundary}
                      style={{
                        weight: 1,
                        opacity: 1,
                        fillOpacity: 0,
                        color: 'red',
                        fillColor: 'red',
                      }}
                    />
                  </LayersControl.Overlay>
                  <LayersControl.Overlay checked name="noiseBoundary">
                    <GeoJSON
                      key={objectHash(noiseBoundary)}
                      data={noiseBoundary}
                      style={{
                        weight: 1,
                        opacity: 1,
                        fillOpacity: 0,
                        color: 'green',
                        fillColor: 'green',
                      }}
                    />
                  </LayersControl.Overlay>
                  {currentUser?.perms?.developer && (
                    <LayersControl.Overlay checked name="drawing">
                      <FeatureGroup>
                        <EditControl
                          position="bottomleft"
                          onEdited={onDrawEditPath}
                          // onCreated={onDrawCreatePath}
                          // onDeleted={onDeleted}
                          // draw={{
                          //   rectangle: false,
                          // }}
                        />
                      </FeatureGroup>
                    </LayersControl.Overlay>
                  )}
                </LayersControl>
              </MapContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  return (
    <div>
      {renderOverlay(pageLoading, settingsMutating, settingsOnline)}
      {renderOffline(settingsOnline)}
      {renderError(pageError)}
      {!pageError && pageLoadedOrRefetching && renderContent()}
    </div>
  );
};

export default NoiseManagementMap;
